/** @jsx jsx */
/** @jsxFrag null */
import moment from 'moment';
import momentTimezone from 'moment-timezone';
import {VNode} from 'snabbdom';
import {jsx} from 'panel';

import {CALENDAR_DATE_FORMAT} from 'common/constants';
import {reporter} from 'common/report/error-reporting';
import {CurrentProject} from 'common/report/model-resources/projects';
import {colors} from 'common/util/colors';
import {getCurrentMoment, parseDate} from 'common/util/date';
import {pluralize} from 'common/util/string';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {DatePickerMode, getDatePickerMode} from 'common/widgets/date-range-picker-new/util';

function getAbbreviatedTimezoneAsOf(date: moment.Moment, timezone: null | string): null | string {
  try {
    return momentTimezone.tz.zone(timezone).abbr(date.valueOf());
  } catch (err) {
    // report the error but don't die because we couldn't parse a timezone
    reporter.error(err);
    return null;
  }
}

function getProjectAsOfDate(currentProject: CurrentProject | undefined): null | moment.Moment {
  if (currentProject?.projectAsOfDate) {
    const parsedDate = parseDate(currentProject?.projectAsOfDate);
    if (!parsedDate) {
      throw new Error(`"projectAsOfDate" does not contain a valid date string`);
    }
    return moment(parsedDate);
  }
  return null;
}

export function getDateRangeTooltipText(
  dateRange: DateRange,
  currentProject: CurrentProject | undefined,
): [string, string] {
  const projectAsOfDate = getProjectAsOfDate(currentProject);
  const {from, to} = dateRange.getCalendarDatesAsOf(
    (projectAsOfDate ?? getCurrentMoment({timezone: currentProject?.timezone})).format(CALENDAR_DATE_FORMAT),
    null,
    null,
    currentProject?.timePeriodSettings,
  );
  const toDate = moment(parseDate(to));
  let fromDate = moment(parseDate(from));

  // for cases where `from` is not defined and asOfDate is past toDate
  if (fromDate.isAfter(toDate)) {
    fromDate = toDate;
  }

  const isSameDate = fromDate.isSame(toDate, `day`);

  const dateRangeText =
    isSameDate || !toDate.isValid()
      ? fromDate.format(`MMM D, YYYY`)
      : `${fromDate.format(`MMM D, YYYY`)} \u002D ${toDate.format(`MMM D, YYYY`)}`;
  const timezoneText = getAbbreviatedTimezoneAsOf(toDate, currentProject?.timezone);

  return [dateRangeText, timezoneText];
}

export function getDateRangeTooltipRenderer(dateRange: DateRange, currentProject: CurrentProject): () => VNode {
  // lazy load this date info because although the date parsing logic is not noticeably slow by itself (maybe ~5ms),
  // it adds up when there's multiple tooltip elements on the page.
  return () => {
    const pickerMode = getDatePickerMode(dateRange);
    if (pickerMode === DatePickerMode.Between) {
      // add +1 to getIntervalDays() because we're showing the full span of the date range, which includes both From & To dates
      const daySpan = dateRange.getIntervalDays() + 1;

      return <>{`${daySpan} ${pluralize(`day`, daySpan)}`}</>;
    }

    const projectAsOfDate = getProjectAsOfDate(currentProject);
    const [dateRangeText, timezoneText] = getDateRangeTooltipText(dateRange, currentProject);
    return (
      <div style={{'text-align': `center`}}>
        <div>
          <span>{dateRangeText}</span>
          &nbsp;
          {currentProject ? <span style={{color: colors.gray3}}>{`(${timezoneText})`}</span> : null}
        </div>
        {projectAsOfDate ? <div>Data last imported {projectAsOfDate.format(`MMM D, YYYY`)}</div> : null}
      </div>
    );
  };
}
